import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Drawer,
  Switch,
  Transfer,
  Button,
  Popconfirm,
  Typography,
  Spin,
  Divider,
  Alert,
  Modal,
  Tag,
  Select,
  Space,
} from "antd";
import {
  getClassroomPrograms,
  updateClassroomProgramEnrollment,
  getClassroomsBySchoolId,
  addClassroomProgram,
  removeClassroomProgram,
} from "../../../../services/classrooms/classrooms.service";
import { ClassroomProgramEnrollmentView } from "../../../../services/classrooms/classrooms.types";
import { useNotification } from "../../../../hooks/useNotification";
import { usePrograms } from "../../../../hooks/usePrograms";

const { Text, Title } = Typography;
const { Option } = Select;

interface ClassProgramsDrawerProps {
  open: boolean;
  onClose: () => void;
  schoolId: number;
  // null while no row has been picked yet — the drawer stays closed in that
  // case (guarded by the parent), kept nullable for prop-wiring convenience.
  classroomId: number | null;
  classroomLabel: string;
  // The group's grade: candidate materias to add are grade-scoped.
  gradeId: number | null;
  // The school's licensed program ids — a group can only carry materias its
  // school is licensed for (same rule the backend enforces).
  licensedProgramIds: number[];
  // Raised after a materia is attached/detached: the classes table renders
  // its Materias column from the API's virtual rows, so the parent has to
  // refetch — reloading this drawer alone leaves the table stale.
  onProgramsChanged?: () => void;
}

interface RosterStudent {
  key: string;
  title: string;
}

// Per-program local edit state, seeded from the server view and only
// committed back via "Guardar" — mirrors the calendar override tab's
// edit-then-save pattern instead of saving on every switch flip.
interface ProgramEntry {
  program_id: number;
  program_title: string;
  subject_name: string;
  savedAllStudents: boolean;
  // Server truth for the currently-persisted restricted subset. The GET
  // endpoint always returns [] here when savedAllStudents is true (verified
  // against classroom-program-enrollment.service.ts — the backend never
  // surfaces a preserved subset once all_students flips back to true), so
  // this field is only meaningful while savedAllStudents is false.
  savedStudentIds: number[];
  // Best-known restricted subset, kept in local memory across all_students
  // toggles within this drawer session. The backend genuinely cannot
  // resurface a preserved subset via GET once all_students=true (see above)
  // — this is the client-side substitute so re-restricting in the SAME
  // session does not start from a blank pane. It does NOT survive a reload
  // of this drawer (that limitation is called out in the revert copy below).
  lastKnownStudentIds: number[];
  localAllStudents: boolean;
  localStudentIds: string[];
  saving: boolean;
}

const toEntry = (view: ClassroomProgramEnrollmentView): ProgramEntry => ({
  program_id: view.program_id,
  program_title: view.program_title,
  subject_name: view.subject.name,
  savedAllStudents: view.all_students,
  savedStudentIds: view.enrolled_student_ids,
  lastKnownStudentIds: view.enrolled_student_ids,
  localAllStudents: view.all_students,
  localStudentIds: view.enrolled_student_ids.map(String),
  saving: false,
});

const isDirty = (entry: ProgramEntry): boolean => {
  if (entry.localAllStudents !== entry.savedAllStudents) return true;
  if (entry.localAllStudents) return false;
  const local = [...entry.localStudentIds].map(Number).sort((a, b) => a - b);
  const saved = [...entry.savedStudentIds].sort((a, b) => a - b);
  return JSON.stringify(local) !== JSON.stringify(saved);
};

// The destructive-direction check (hardening backlog item 1): a save REDUCES
// access whenever it moves away from "everyone" or drops a currently-active
// student from an already-restricted subset. Moving TO all_students never
// reduces access (that direction keeps its own, lighter confirmation below).
const wouldReduceAccess = (entry: ProgramEntry): boolean => {
  if (entry.localAllStudents) return false;
  if (entry.savedAllStudents) return true;
  const nextIds = new Set(entry.localStudentIds.map(Number));
  return entry.savedStudentIds.some((id) => !nextIds.has(id));
};

// The single most dangerous shape: a restricted save with zero students
// revokes the program for every student in the group.
const wouldRevokeEveryone = (entry: ProgramEntry): boolean =>
  !entry.localAllStudents && entry.localStudentIds.length === 0;

const needsSaveConfirm = (entry: ProgramEntry): boolean =>
  isDirty(entry) && wouldReduceAccess(entry);

/**
 * "Programas" drawer for a single group (ClassesTab management row), design
 * #860 §7: per licensed program, either every student in the group is
 * enrolled (all_students, the default/hot path) or a restricted subset
 * managed here via a Transfer of the group's own active students.
 */
const ClassProgramsDrawer: React.FC<ClassProgramsDrawerProps> = ({
  open,
  onClose,
  schoolId,
  classroomId,
  classroomLabel,
  gradeId,
  licensedProgramIds,
  onProgramsChanged,
}) => {
  const { notify } = useNotification();
  const { programs: allPrograms } = usePrograms();
  const [addProgramId, setAddProgramId] = useState<number | undefined>(
    undefined
  );
  const [adding, setAdding] = useState(false);
  const [removingProgramId, setRemovingProgramId] = useState<number | null>(
    null
  );
  // Context-based Modal API: AntD v5 static Modal.confirm does not render
  // under React 19 (see SchoolProgramsSelect.tsx / Settings/index.tsx for the
  // same pattern already established in this repo).
  const [modal, modalContextHolder] = Modal.useModal();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [entries, setEntries] = useState<ProgramEntry[]>([]);
  const [roster, setRoster] = useState<RosterStudent[]>([]);

  useEffect(() => {
    if (!open || !classroomId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const [programs, classrooms] = await Promise.all([
          getClassroomPrograms(classroomId),
          getClassroomsBySchoolId(schoolId),
        ]);
        if (cancelled) return;

        setEntries(programs.map(toEntry));

        const classroom = classrooms.find((c) => c.id === classroomId);
        const students = (classroom?.users ?? []).filter(
          (u: any) => u.role === "STUDENT"
        );
        setRoster(
          students.map((u: any) => ({
            key: String(u.id),
            title: `${u.first_name} ${u.last_name}`,
          }))
        );
      } catch (error) {
        if (cancelled) return;
        console.error("Error loading classroom programs:", error);
        setLoadError(true);
        notify(
          "error",
          "Error",
          "No se pudieron cargar las asignaturas del grupo."
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, classroomId, schoolId, reloadToken]);

  // Candidate materias to add: licensed to the school, same grade as the
  // group, not SEL (amendment #889 — SEL is orthogonal to classrooms and the
  // backend rejects it anyway), and not already actively attached.
  // GET /programs serves { grade: { id }, subject: { id, name } } — there is
  // no flat grade_id nor subject.code in the payload, despite ProgramEntity
  // claiming both; resolve through the nested objects with the typed flat
  // field as fallback.
  const addablePrograms = useMemo(() => {
    const attached = new Set(entries.map((e) => e.program_id));
    const licensed = new Set(licensedProgramIds);
    return allPrograms.filter(
      (p) =>
        licensed.has(p.id) &&
        (p.grade?.id ?? p.grade_id) === gradeId &&
        p.subject?.code !== "SEL" &&
        p.subject?.name !== "Socioemocional" &&
        !attached.has(p.id)
    );
  }, [allPrograms, licensedProgramIds, gradeId, entries]);

  const handleAddProgram = async () => {
    if (!classroomId || !addProgramId) return;
    setAdding(true);
    try {
      const added = await addClassroomProgram(classroomId, addProgramId);
      notify(
        "success",
        "Asignatura agregada",
        `${added.program_title} quedó asignada al grupo con todos los estudiantes.`
      );
      setAddProgramId(undefined);
      setReloadToken((t) => t + 1);
      onProgramsChanged?.();
    } catch (error: any) {
      notify(
        "error",
        "Error",
        error?.response?.data?.message ?? "No se pudo agregar la asignatura."
      );
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveProgram = async (programId: number) => {
    if (!classroomId) return;
    setRemovingProgramId(programId);
    try {
      await removeClassroomProgram(classroomId, programId);
      notify(
        "success",
        "Asignatura quitada",
        "La asignatura dejó de ofrecerse en el grupo. El contenido histórico no se borra."
      );
      setReloadToken((t) => t + 1);
      onProgramsChanged?.();
    } catch (error: any) {
      notify(
        "error",
        "Error",
        error?.response?.data?.message ?? "No se pudo quitar la asignatura."
      );
    } finally {
      setRemovingProgramId(null);
    }
  };

  const updateEntry = (programId: number, patch: Partial<ProgramEntry>) => {
    setEntries((prev) =>
      prev.map((entry) =>
        entry.program_id === programId ? { ...entry, ...patch } : entry
      )
    );
  };

  const handleSwitchChange = (programId: number, checked: boolean) => {
    setEntries((prev) =>
      prev.map((entry) => {
        if (entry.program_id !== programId) return entry;
        if (checked) return { ...entry, localAllStudents: true };
        // Switching to restricted: seed the Transfer from the best-known
        // subset (the currently-active local selection, or — after an
        // in-session revert to all_students — the last known list before
        // that revert) so the admin never lands on a blank pane by default.
        const seeded =
          entry.localStudentIds.length > 0
            ? entry.localStudentIds
            : entry.lastKnownStudentIds.map(String);
        return { ...entry, localAllStudents: false, localStudentIds: seeded };
      })
    );
  };

  const handleTransferChange = (programId: number, nextTargetKeys: string[]) => {
    updateEntry(programId, { localStudentIds: nextTargetKeys });
  };

  // Explicit discard of a pending edit — used ONLY by the close-guard's
  // "Cerrar sin guardar" action below. Popconfirm's own Cancel button must
  // NEVER call this: "Cancelar" on a save confirmation means "no guardar
  // todavía", not "throw away what I curated" (hardening backlog item 2).
  const handleDiscardPendingEdit = (programId: number) => {
    setEntries((prev) =>
      prev.map((entry) =>
        entry.program_id === programId
          ? {
              ...entry,
              localAllStudents: entry.savedAllStudents,
              localStudentIds: entry.savedStudentIds.map(String),
            }
          : entry
      )
    );
  };

  const handleSave = async (programId: number) => {
    const entry = entries.find((e) => e.program_id === programId);
    if (!classroomId || !entry) return;

    updateEntry(programId, { saving: true });
    try {
      const result = await updateClassroomProgramEnrollment(
        classroomId,
        programId,
        entry.localAllStudents
          ? { all_students: true }
          : {
              all_students: false,
              student_ids: entry.localStudentIds.map(Number),
            }
      );

      if (result.all_students) {
        // The backend intentionally never surfaces a preserved subset once
        // all_students=true (relicense-preserving semantics — rows exist but
        // are not read here). Keep lastKnownStudentIds/localStudentIds as
        // they were so re-restricting in this same session starts from the
        // last known list instead of an empty one.
        updateEntry(programId, {
          saving: false,
          savedAllStudents: true,
          savedStudentIds: [],
          localAllStudents: true,
        });
      } else {
        updateEntry(programId, {
          saving: false,
          savedAllStudents: false,
          savedStudentIds: result.enrolled_student_ids,
          lastKnownStudentIds: result.enrolled_student_ids,
          localAllStudents: false,
          localStudentIds: result.enrolled_student_ids.map(String),
        });
      }
      notify("success", "Éxito", "Inscripción actualizada correctamente.");
    } catch (error) {
      console.error("Error updating classroom program enrollment:", error);
      updateEntry(programId, { saving: false });
      notify("error", "Error", "No se pudo guardar la inscripción.");
    }
  };

  // A revert to all_students=true is destructive to the admin's mental model
  // even though the backend preserves the subset rows (relicense-preserving
  // semantics) — a light confirmation, not the same guard as restricting.
  const needsRevertConfirm = (entry: ProgramEntry): boolean =>
    entry.localAllStudents &&
    entry.savedAllStudents === false &&
    entry.savedStudentIds.length > 0;

  const anyDirty = entries.some(isDirty);
  // While a save is in flight the drawer is not dismissable, so the admin stays
  // on it until the pending request reports its outcome.
  // Mirrors GenerarClasesModal's closable/maskClosable/keyboard pattern.
  const anySaving = entries.some((entry) => entry.saving);

  // Unsaved-changes guard on close (hardening backlog item 3): the parent
  // unmounts this drawer on X / mask click / Escape, which all funnel
  // through AntD Drawer's single onClose — discarding pending edits
  // silently otherwise.
  const handleRequestClose = useCallback(() => {
    if (anySaving) return;
    if (!anyDirty) {
      onClose();
      return;
    }
    modal.confirm({
      title: "Cambios sin guardar",
      content:
        "Hay cambios sin guardar en este panel de asignaturas. Si se cierra ahora, se perderán.",
      okText: "Cerrar sin guardar",
      cancelText: "Seguir editando",
      onOk: () => {
        entries.forEach((entry) => handleDiscardPendingEdit(entry.program_id));
        onClose();
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyDirty, anySaving, onClose]);

  return (
    <Drawer
      title={`Asignaturas — ${classroomLabel}`}
      open={open}
      onClose={handleRequestClose}
      width={520}
      closable={!anySaving}
      maskClosable={!anySaving}
      keyboard={!anySaving}
      destroyOnClose
    >
      {modalContextHolder}
      {loading ? (
        <Spin />
      ) : loadError ? (
        <Alert
          type="error"
          showIcon
          message="No se pudieron cargar las asignaturas del grupo."
          description="Puede deberse a un problema de conexión o del servidor."
          action={
            <Button size="small" onClick={() => setReloadToken((t) => t + 1)}>
              Reintentar
            </Button>
          }
        />
      ) : (
        <>
          <Space.Compact style={{ width: "100%", marginBottom: 16 }}>
            <Select
              placeholder="Agregar asignatura al grupo"
              value={addProgramId}
              onChange={(value) => setAddProgramId(value)}
              style={{ flex: 1 }}
              disabled={adding || addablePrograms.length === 0}
              showSearch
              optionFilterProp="children"
            >
              {addablePrograms.map((program) => (
                <Option key={program.id} value={program.id}>
                  {program.title}
                </Option>
              ))}
            </Select>
            <Button
              type="primary"
              disabled={!addProgramId}
              loading={adding}
              onClick={handleAddProgram}
            >
              Agregar
            </Button>
          </Space.Compact>

          {entries.map((entry, index) => {
          const dirty = isDirty(entry);
          const saveConfirmNeeded = needsSaveConfirm(entry);
          const revertConfirmNeeded = !saveConfirmNeeded && needsRevertConfirm(entry);

          const guardarButton = (
            <Button
              type="primary"
              disabled={!dirty || entry.saving}
              loading={entry.saving}
              onClick={() => {
                if (!saveConfirmNeeded && !revertConfirmNeeded) {
                  handleSave(entry.program_id);
                }
              }}
            >
              Guardar
            </Button>
          );

          let guardarControl = guardarButton;
          if (saveConfirmNeeded) {
            const revokesEveryone = wouldRevokeEveryone(entry);
            guardarControl = (
              <Popconfirm
                title={
                  revokesEveryone
                    ? "Esta acción quitará el acceso a esta asignatura para TODOS los estudiantes del grupo — no quedará nadie inscrito. ¿Continuar?"
                    : "Esta acción reducirá el acceso a esta asignatura: uno o más estudiantes actualmente inscritos lo perderán. ¿Continuar?"
                }
                okText={revokesEveryone ? "Sí, quitar a todos" : "Sí, continuar"}
                cancelText="Cancelar"
                onConfirm={() => handleSave(entry.program_id)}
              >
                {guardarButton}
              </Popconfirm>
            );
          } else if (revertConfirmNeeded) {
            guardarControl = (
              <Popconfirm
                title="Se activará el acceso para todos los estudiantes del grupo. La lista restringida actual dejará de aplicarse y solo se recordará mientras este panel permanezca abierto."
                okText="Sí, activar para todos"
                cancelText="Cancelar"
                onConfirm={() => handleSave(entry.program_id)}
              >
                {guardarButton}
              </Popconfirm>
            );
          }

          return (
            <div key={entry.program_id} data-testid="program-row">
              {index > 0 && <Divider />}
              <Title level={5} style={{ marginBottom: 4 }}>
                {entry.subject_name}
              </Title>
              <Text type="secondary">{entry.program_title}</Text>
              {/* A dirty row must never be mistaken for the true server
                  state — the switch/Transfer alone can look identical to a
                  saved state that isn't. */}
              {dirty && (
                <Tag color="warning" style={{ marginLeft: 8 }}>
                  Sin guardar
                </Tag>
              )}

              <div style={{ margin: "12px 0" }}>
                <Switch
                  checked={entry.localAllStudents}
                  disabled={entry.saving}
                  onChange={(checked) =>
                    handleSwitchChange(entry.program_id, checked)
                  }
                />
                <span style={{ marginLeft: 8 }}>Todos los estudiantes</span>
              </div>

              {!entry.localAllStudents && (
                <Transfer
                  dataSource={roster}
                  targetKeys={entry.localStudentIds}
                  disabled={entry.saving}
                  onChange={(nextTargetKeys) =>
                    handleTransferChange(
                      entry.program_id,
                      nextTargetKeys as string[]
                    )
                  }
                  render={(item) => item.title}
                  titles={["Disponibles", "Inscritos"]}
                  listStyle={{ width: 220, height: 240 }}
                />
              )}

              <div style={{ marginTop: 12 }}>
                <Space>
                  {guardarControl}
                  <Popconfirm
                    title="Se quitará la asignatura del grupo: dejará de ofrecerse a alumnos y profes. El contenido histórico no se borra y se puede volver a agregar."
                    okText="Sí, quitar"
                    cancelText="Cancelar"
                    onConfirm={() => handleRemoveProgram(entry.program_id)}
                  >
                    <Button
                      danger
                      disabled={entry.saving || removingProgramId !== null}
                      loading={removingProgramId === entry.program_id}
                    >
                      Quitar asignatura
                    </Button>
                  </Popconfirm>
                </Space>
              </div>
            </div>
          );
          })}
        </>
      )}
    </Drawer>
  );
};

export default ClassProgramsDrawer;
